// CNC 파트너 — 케이스 상세 우측 패널 (2026-10-23, UI 구조 단계)
// 3D 미리보기(디자인+지그 겹침) 자리 · 파일 · 기공소 메모 · 지그 실패 사유 · 재생성 요청 · 상태 전환
import React from 'react';
import { X, Box, Download, RefreshCw, AlertTriangle, Truck, MessageSquare, RotateCcw, Image as ImageIcon } from 'lucide-react';
import { STATUS, STATUS_ORDER, JIG, KindChip, StatusBadge, ddayText, isDueSoon, fmtMdHm, fileNameOf, detailRows, patientNameOf, ui } from './partnerUi';
import { CNC_NAME_ON } from './partnerSample';
import { teethLabel } from '../../services/outsourceMfApi';

function PartnerCasePanel({ row, onClose, onStatus, mobile }) {
    if (!row) return null;
    const soon = isDueSoon(row);
    const jig = row.jig ? JIG[row.jig] : null;
    const rows = detailRows(row);

    return (
        <>
            <div style={styles.backdrop} onClick={onClose} />
            <aside style={{ ...styles.panel, ...(mobile ? styles.panelMobile : {}) }} aria-label="케이스 상세">
                <div style={styles.head}>
                    <div style={{ minWidth: 0 }}>
                        {/* 케이스 = 환자 이름(대표 10-23, 화면 표시만). CNC는 실명 스위치 전 숨김 */}
                        {patientNameOf(row, CNC_NAME_ON)
                            ? <div style={styles.patient}>{patientNameOf(row, CNC_NAME_ON)}</div>
                            : <div style={styles.patientHidden}>{'케이스 번호로 확인 (CNC — 약관 개정 후 환자 이름 표시)'}</div>}
                        <div style={styles.caseNo}>{row.caseNo}</div>
                        <div style={styles.headSub}><KindChip kind={row.kind} /><span>{`${row.labName} · ${teethLabel(row.teeth, row.bridge)}`}</span></div>
                    </div>
                    <button type="button" style={styles.closeBtn} onClick={onClose} aria-label="닫기"><X size={18} /></button>
                </div>

                <div style={styles.scroll}>
                    {/* 3D 미리보기 — 목록은 사진, 여기서만 3D를 연다 */}
                    <div style={styles.preview}>
                        <Box size={36} color="#94a3b8" />
                        <div style={styles.previewText}>{'3D 미리보기'}</div>
                        <div style={ui.muted}>{row.kind === 'cnc' ? '디자인(어버트먼트) + 지그 겹친 상태 · 회전·확대' : '디자인 파일 · 회전·확대'}</div>
                    </div>

                    {/* 의뢰 내용 — 기공소가 외주 의뢰서에 적은 그대로 */}
                    <section style={styles.section}>
                        <div style={styles.secTitle}>{'의뢰 내용'}</div>
                        <div style={styles.kv}><span>{'치아'}</span><span>{teethLabel(row.teeth, row.bridge)}</span></div>
                        {rows.map(([k, v]) => (
                            <div key={k} style={styles.kv}><span>{k}</span><span style={styles.kvVal}>{v}</span></div>
                        ))}
                        {row.note && (
                            <div style={styles.noteBox}>
                                <div style={{ ...styles.secTitle, display: 'flex', alignItems: 'center', gap: 6 }}><MessageSquare size={13} />{'요청사항'}</div>
                                <p style={styles.memo}>{row.note}</p>
                            </div>
                        )}
                    </section>

                    {/* 상태 전환 */}
                    <section style={styles.section}>
                        <div style={styles.secTitle}>{'상태'}</div>
                        <div style={styles.statusRow}>
                            {STATUS_ORDER.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => onStatus(row.id, s)}
                                    style={{
                                        ...styles.statusBtn,
                                        ...(row.status === s ? { color: STATUS[s].fg, backgroundColor: STATUS[s].bg, borderColor: STATUS[s].fg } : {}),
                                    }}
                                >
                                    {s === 'shipped' && <Truck size={13} />}{STATUS[s].label}
                                </button>
                            ))}
                        </div>
                        <p style={ui.cardSub}>{'바꾸면 기공소 화면에도 바로 반영됩니다. 출고된 건은 이번 달 명세서에 자동으로 들어갑니다.'}</p>
                        {row.status === 'shipped' && (
                            <label style={styles.field}>
                                <span style={styles.fieldLabel}>{'택배 송장번호'}</span>
                                <input style={styles.input} defaultValue={row.trackingNo} placeholder="송장번호 입력 (선택)" />
                            </label>
                        )}
                        {row.kind === 'cnc' && row.status !== 'received' && (
                            <label style={styles.field}>
                                <span style={styles.fieldLabel}>{'사용 블랭크 허가번호 · LOT (CNC)'}</span>
                                <input style={styles.input} placeholder="예: 제허 00-000호 / LOT 2026A1234" />
                                <span style={ui.muted}>{'프리밀드 블랭크 추적용 — 기공소 명세서·이력에 함께 남습니다.'}</span>
                            </label>
                        )}
                    </section>

                    {/* 일정 */}
                    <section style={styles.section}>
                        <div style={styles.secTitle}>{'일정'}</div>
                        <div style={styles.kv}><span>{'접수'}</span><span>{fmtMdHm(row.receivedAt)}</span></div>
                        <div style={styles.kv}>
                            <span>{'납기'}</span>
                            <span style={soon ? styles.red : undefined}>{`${fmtMdHm(row.dueAt)} (${ddayText(row.dueAt)})`}</span>
                        </div>
                        <div style={styles.kv}><span>{'현재'}</span><StatusBadge status={row.status} /></div>
                    </section>

                    {/* 파일 */}
                    <section style={styles.section}>
                        <div style={styles.secTitle}>{'파일'}</div>
                        <div style={styles.fileRow}>
                            <span style={styles.fileName}>{fileNameOf(row, 'abutment')}</span>
                            <button type="button" style={styles.iconBtn} onClick={() => alert('미리보기: 다운로드는 서버 연결 후 동작합니다.')}><Download size={14} /></button>
                        </div>
                        {jig && <div style={styles.fileRow}>
                            {row.jig === 'ready' ? (
                                <>
                                    <span style={styles.fileName}>{fileNameOf(row, 'jig')}</span>
                                    <button type="button" style={styles.iconBtn} onClick={() => alert('미리보기: 다운로드는 서버 연결 후 동작합니다.')}><Download size={14} /></button>
                                </>
                            ) : (
                                <span style={{ ...styles.fileName, color: jig.fg }}>{`지그 ${jig.label}`}</span>
                            )}
                        </div>}
                        <p style={ui.cardSub}>{'받은 파일은 첫 다운로드 후 24시간이 지나면 서버에서 지워집니다.'}</p>
                    </section>

                    {/* 재제작·클레임 — 원케이스에 연결, 제조사가 무상/유상 판정 */}
                    {row.remake && (
                        <section style={{ ...styles.section, ...styles.failBox }}>
                            <div style={{ ...styles.secTitle, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <RotateCcw size={14} />{row.remake.status === 'requested' ? '기공소 재제작 요청' : '재제작 처리됨 (무상)'}
                            </div>
                            <p style={{ ...ui.cardSub, color: '#7f1d1d' }}>{row.remake.reason}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#7f1d1d' }}>
                                <ImageIcon size={12} />{`사진 ${row.remake.photos}장 · ${fmtMdHm(row.remake.at)}`}
                            </div>
                            {row.remake.status === 'requested' && (
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <button type="button" style={ui.ghostBtn} onClick={() => alert('미리보기: 무상 재제작으로 접수됩니다(명세서에 0원).')}>{'무상 재제작'}</button>
                                    <button type="button" style={ui.ghostBtn} onClick={() => alert('미리보기: 유상 재제작으로 접수됩니다(명세서에 단가 반영).')}>{'유상 재제작'}</button>
                                </div>
                            )}
                            <p style={{ ...ui.muted, margin: 0 }}>{'재제작 이력은 원케이스에 연결돼 쌓이고, 무상 건은 명세서에 0원으로 들어갑니다.'}</p>
                        </section>
                    )}

                    {/* 지그 실패 */}
                    {row.jig === 'failed' && (
                        <section style={{ ...styles.section, ...styles.failBox }}>
                            <div style={{ ...styles.secTitle, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <AlertTriangle size={14} />{'지그 생성 실패'}
                            </div>
                            <p style={{ ...ui.cardSub, color: '#7f1d1d' }}>{row.jigFailReason || '사유 확인 중'}</p>
                            <button type="button" style={ui.ghostBtn} onClick={() => alert('미리보기: 재생성 요청은 서버 연결 후 동작합니다.')}>
                                <RefreshCw size={14} />{'재생성 요청'}
                            </button>
                        </section>
                    )}

                </div>
            </aside>
        </>
    );
}

const styles = {
    backdrop: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.25)', zIndex: 1000 },
    panel: {
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '100%',
        backgroundColor: '#fff', boxShadow: '-8px 0 24px rgba(15,23,42,0.12)', zIndex: 1001,
        display: 'flex', flexDirection: 'column',
    },
    panelMobile: { width: '100%' },
    head: {
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10,
        padding: '16px 18px', borderBottom: '1px solid #e2e8f0',
    },
    patient: { fontSize: 18, fontWeight: 800, color: '#0f172a' },
    patientHidden: { fontSize: 13, fontWeight: 600, color: '#94a3b8' },
    caseNo: { fontSize: 12, fontWeight: 700, color: '#64748b', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', marginTop: 2 },
    headSub: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', marginTop: 6 },
    closeBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 4 },
    scroll: { flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 16 },
    preview: {
        height: 220, borderRadius: 12, backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
    },
    previewText: { fontSize: 14, fontWeight: 700, color: '#475569' },
    section: { display: 'flex', flexDirection: 'column', gap: 8 },
    secTitle: { fontSize: 13, fontWeight: 700, color: '#334155' },
    statusRow: { display: 'flex', gap: 6 },
    statusBtn: {
        flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        padding: '9px 0', border: '1px solid #e2e8f0', borderRadius: 9, backgroundColor: '#fff',
        color: '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    },
    field: { display: 'flex', flexDirection: 'column', gap: 4 },
    fieldLabel: { fontSize: 12, color: '#64748b' },
    input: { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 },
    kv: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#334155' },
    red: { color: '#dc2626', fontWeight: 700 },
    kvVal: { fontWeight: 600, color: '#0f172a', textAlign: 'right' },
    noteBox: { padding: 12, borderRadius: 10, backgroundColor: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', flexDirection: 'column', gap: 6 },
    fileRow: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        padding: '8px 10px', borderRadius: 8, backgroundColor: '#f8fafc', border: '1px solid #f1f5f9',
    },
    fileName: { fontSize: 12, color: '#334155', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' },
    iconBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28,
        border: '1px solid #cbd5e1', borderRadius: 7, backgroundColor: '#fff', cursor: 'pointer', color: '#334155', flexShrink: 0,
    },
    failBox: { padding: 12, borderRadius: 10, backgroundColor: '#fef2f2', border: '1px solid #fecaca' },
    memo: { margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
};

export default PartnerCasePanel;
